import { roleLabel, errorLabel } from './display-copy';
import { useEffect, useState } from 'react';
import type { ActiveSession } from '../../lib/auth';
import { runtimeMode } from '../../lib/auth';
import { contextInventory, createContext } from '../../lib/oem-client';
export function ContextCreation({ session }: { readonly session: ActiveSession }) {
  const parents = [...session.organizationContexts, ...session.customerContexts].filter((p) =>
    p.actions.some((a) => a.startsWith('create_')),
  );
  const [parent, setParent] = useState(0),
    [action, setAction] = useState('create_project'),
    [label, setLabel] = useState(''),
    [source, setSource] = useState(''),
    [customer, setCustomer] = useState(session.contexts[0]?.customerId ?? ''),
    [brand, setBrand] = useState(''),
    [skus, setSkus] = useState('SKU-P30\nSKU-J50'),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [result, setResult] = useState('');
  const [inventory, setInventory] = useState<Awaited<ReturnType<typeof contextInventory>>>(
    runtimeMode === 'fixture'
      ? {
          customers: session.contexts.map((c) => ({
            id: c.customerId,
            organizationId: c.organizationId,
            label: c.label,
          })),
          brands: [],
        }
      : { customers: [], brands: [] },
  );
  useEffect(() => {
    if (runtimeMode === 'connected')
      void contextInventory()
        .then(setInventory)
        .catch(() => setError('โหลดข้อมูลลูกค้าไม่ได้'));
  }, [session]);
  if (!parents.length) return null;
  const selected = parents[parent] ?? parents[0];
  if (!selected) return null;
  async function save() {
    setBusy(true);
    setError('');
    try {
      if (runtimeMode === 'fixture') {
        setResult('แบบฟอร์มนี้ใช้กับระบบที่เชื่อมต่อแล้ว ข้อมูลตัวอย่างไม่สร้างโครงการจริง');
        return;
      }
      const r = await createContext(action, {
        organizationId: parents[parent]?.organizationId,
        customerId: action === 'create_customer' ? null : customer,
        idempotencyKey: crypto.randomUUID(),
        payload: {
          label,
          source,
          ...(brand ? { brandId: brand } : {}),
          ...(action === 'create_project'
            ? {
                skuLabels: skus
                  .split('\n')
                  .map((x) => x.trim())
                  .filter(Boolean),
              }
            : {}),
          ...(action === 'create_sku' ? { projectId: session.contexts[0]?.projectId } : {}),
        },
      });
      setResult(`บันทึกพื้นที่ทำงานแล้ว · รหัส ${r.id} · หลักฐาน ${r.receiptId}`);
      setInventory(await contextInventory());
      if (action === 'create_project') {
        localStorage.setItem('sqi-active-project', r.id);
        window.location.assign('/app');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unavailable');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <div className="eyebrow">NEW INQUIRY</div>
      <h2>เพิ่มข้อมูลในพื้นที่ที่ได้รับสิทธิ์</h2>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label>
          พื้นที่ทำงาน
          <select value={parent} onChange={(e) => setParent(Number(e.target.value))}>
            {parents.map((p, i) => (
              <option value={i} key={p.organizationId + String(i)}>
                {p.label ?? p.organizationId} · {roleLabel(p.role)}
              </option>
            ))}
          </select>
        </label>
        <label>
          สร้าง
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            {selected.actions
              .filter((a) => a.startsWith('create_'))
              .map((a) => (
                <option key={a} value={a}>
                  {
                    {
                      create_customer: 'ลูกค้า',
                      create_brand: 'แบรนด์',
                      create_project: 'โปรเจกต์ / Inquiry',
                      create_sku: 'SKU',
                    }[a]
                  }
                </option>
              ))}
          </select>
        </label>
        {action !== 'create_customer' ? (
          <label>
            ลูกค้า
            <select value={customer} onChange={(e) => setCustomer(e.target.value)} required>
              <option value="">เลือกลูกค้า</option>
              {inventory.customers
                .filter((c) => c.organizationId === selected.organizationId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        <label>
          ชื่อ
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={300}
          />
        </label>
        <label>
          แหล่งข้อมูล / ขอบเขต
          <textarea value={source} onChange={(e) => setSource(e.target.value)} maxLength={4000} />
        </label>
        {action === 'create_project' ? (
          <>
            <label>
              แบรนด์
              <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">ยังไม่ระบุแบรนด์</option>
                {inventory.brands
                  .filter((b) => b.customerId === customer)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              SKU · หนึ่งรายการต่อบรรทัด
              <textarea value={skus} onChange={(e) => setSkus(e.target.value)} required />
            </label>
          </>
        ) : null}
        <button className="action" disabled={busy || !navigator.onLine}>
          บันทึกข้อมูลใหม่
        </button>
      </form>
      {error ? (
        <p role="alert" className="note-danger">
          {errorLabel(error)}
        </p>
      ) : null}
      {result ? (
        <p role="status" className="note-warning">
          {result}
        </p>
      ) : null}
    </section>
  );
}
